import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { useServices, useSolutions, useBranding, usePageText, useSiteSettings } from "@/hooks/useCms";
import WhatsAppIcon from "@/components/icons/WhatsAppIcon";
import { waHref } from "@/lib/whatsapp";

const STATIC_COLS = [
  {
    title: "Empresa",
    items: [
      { label: "O Grupo", to: "/sobre/grupo" },
      { label: "Certificações", to: "/sobre/certificacoes" },
      { label: "Notícias", to: "/noticias" },
      { label: "Oportunidades", to: "/sobre/oportunidades" },
      { label: "Academia", to: "/academia" },
      { label: "Blog", to: "/sobre/blog" },
      { label: "Contactos", to: "/contactos" },
    ],
  },
];

export default function Footer() {
  const { data: services } = useServices();
  const { data: solutions } = useSolutions();
  const { logoLight, logoDark } = useBranding();
  const settings = useSiteSettings();
  const footerEmail = settings?.footer_email ?? "";
  const footerEmail2 = settings?.footer_email2 ?? "";
  const footerPhone = settings?.footer_phone ?? "";
  const footerPhone2 = settings?.footer_phone2 ?? "";
  const footerWhatsapp = settings?.footer_whatsapp ?? "";
  const footerWhatsappMessage = settings?.footer_whatsapp_message ?? "";
  const footerAddr1 = settings?.footer_address_line1 ?? "";
  const footerAddr2 = settings?.footer_address_line2 ?? "";
  const footerEmailNote = settings?.footer_email_note || "";
  const footerEmail2Note = settings?.footer_email2_note || "";
  const footerPhoneNote = settings?.footer_phone_note || "";
  const footerPhone2Note = settings?.footer_phone2_note || "";
  const footerWhatsappNote = settings?.footer_whatsapp_note || "";
  const footerAddressNote = settings?.footer_address_note || "";
  const solutionsLimit = Math.max(1, Number(settings?.footer_solutions_limit) || 6);
  const servicesLimit = Math.max(1, Number(settings?.footer_services_limit) || 6);
  const telHref = footerPhone ? `tel:${footerPhone.replace(/\s+/g, "")}` : "";
  const telHref2 = footerPhone2 ? `tel:${footerPhone2.replace(/\s+/g, "")}` : "";
  const waHrefFooter = waHref(footerWhatsapp, footerWhatsappMessage);
  const { data: footerText } = usePageText("footer-tagline", {
    intro_paragraphs: [
      "Serviços de inspeção, certificação e engenharia para garantir a segurança, conformidade, fiabilidade e proteção de pessoas, ativos e operações.",
    ],
  });
  const tagline = (footerText?.intro_paragraphs && footerText.intro_paragraphs[0]) ||
    "Serviços de inspeção, certificação e engenharia para garantir a segurança, conformidade, fiabilidade e proteção de pessoas, ativos e operações.";

  const solucoesItems = (solutions || [])
    .filter((s) => s.show_in_footer !== false)
    .slice()
    .sort((a, b) => (a.footer_order ?? 0) - (b.footer_order ?? 0))
    .slice(0, solutionsLimit)
    .map((s) => ({
      label: s.title,
      to: `/solucoes/${s.slug}`,
    }));
  if (solucoesItems.length > 0) {
    solucoesItems.push({ label: "Ver todas as soluções", to: "/solucoes" });
  }

  const servicosItems = (services || [])
    .filter((s) => s.show_in_footer !== false)
    .slice()
    .sort((a, b) => (a.footer_order ?? 0) - (b.footer_order ?? 0))
    .slice(0, servicesLimit)
    .map((s) => ({
      label: s.title,
      to: `/servicos#${s.slug}`,
    }));
  if (servicosItems.length > 0) {
    servicosItems.push({ label: "Ver todos os serviços", to: "/servicos" });
  }

  const COLS = [
    { title: "Soluções", items: solucoesItems.length ? solucoesItems : [{ label: "Ver soluções", to: "/solucoes" }] },
    { title: "Serviços", items: servicosItems.length ? servicosItems : [{ label: "Ver serviços", to: "/servicos" }] },
    ...STATIC_COLS,
  ];

  return (
    <footer className="border-t border-slate-200 dark:border-white/10 bg-[#F9FAFB] dark:bg-[#0D1320]" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-2 lg:grid-cols-5 gap-10">
        {/* Coluna marca */}
        <div className="lg:col-span-2">
          <div className="relative h-8 w-[180px]">
            <img
              src={logoLight}
              alt="INXPEK"
              className="absolute inset-0 h-full w-full object-contain object-left dark:opacity-0 transition-opacity"
            />
            <img
              src={logoDark}
              alt="INXPEK"
              className="absolute inset-0 h-full w-full object-contain object-left opacity-0 dark:opacity-100 transition-opacity"
            />
          </div>
          <p className="mt-4 text-sm text-slate-500 dark:text-slate-400 max-w-sm leading-relaxed" data-testid="footer-tagline" dangerouslySetInnerHTML={{ __html: tagline }} />

          <ul className="mt-6 space-y-1.5 text-sm text-slate-600 dark:text-slate-300">
            {footerEmail && (
              <li className="flex items-start gap-2.5">
                <Mail size={13} className="text-[#1698AC] dark:text-[#4FC6D8] mt-0.5" />
                <div className="min-w-0">
                  <a href={`mailto:${footerEmail}`} className="hover:text-[#1698AC] dark:hover:text-[#4FC6D8] transition-colors block leading-tight" data-testid="footer-email">
                    {footerEmail}
                  </a>
                  {footerEmailNote && (
                    <span className="text-[11px] text-slate-400 dark:text-slate-500 block mt-0.5" data-testid="footer-email-note">
                      {footerEmailNote}
                    </span>
                  )}
                </div>
              </li>
            )}
            {footerEmail2 && (
              <li className="flex items-start gap-2.5">
                <Mail size={13} className="text-[#1698AC] dark:text-[#4FC6D8] mt-0.5" />
                <div className="min-w-0">
                  <a href={`mailto:${footerEmail2}`} className="hover:text-[#1698AC] dark:hover:text-[#4FC6D8] transition-colors block leading-tight" data-testid="footer-email2">
                    {footerEmail2}
                  </a>
                  {footerEmail2Note && (
                    <span className="text-[11px] text-slate-400 dark:text-slate-500 block mt-0.5" data-testid="footer-email2-note">
                      {footerEmail2Note}
                    </span>
                  )}
                </div>
              </li>
            )}
            {footerPhone && (
              <li className="flex items-start gap-2.5">
                <Phone size={13} className="text-[#1698AC] dark:text-[#4FC6D8] mt-0.5" />
                <div className="min-w-0">
                  <a href={telHref} className="hover:text-[#1698AC] dark:hover:text-[#4FC6D8] transition-colors block leading-tight" data-testid="footer-phone">
                    {footerPhone}
                  </a>
                  {footerPhoneNote && (
                    <span className="text-[11px] text-slate-400 dark:text-slate-500 block mt-0.5" data-testid="footer-phone-note">
                      {footerPhoneNote}
                    </span>
                  )}
                </div>
              </li>
            )}
            {footerPhone2 && (
              <li className="flex items-start gap-2.5">
                <Phone size={13} className="text-[#1698AC] dark:text-[#4FC6D8] mt-0.5" />
                <div className="min-w-0">
                  <a href={telHref2} className="hover:text-[#1698AC] dark:hover:text-[#4FC6D8] transition-colors block leading-tight" data-testid="footer-phone2">
                    {footerPhone2}
                  </a>
                  {footerPhone2Note && (
                    <span className="text-[11px] text-slate-400 dark:text-slate-500 block mt-0.5" data-testid="footer-phone2-note">
                      {footerPhone2Note}
                    </span>
                  )}
                </div>
              </li>
            )}
            {waHrefFooter && (
              <li className="flex items-start gap-2.5">
                <WhatsAppIcon size={13} className="text-[#25D366] mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <a
                    href={waHrefFooter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-[#25D366] transition-colors block leading-tight"
                    data-testid="footer-whatsapp"
                  >
                    {footerWhatsapp}
                  </a>
                  {footerWhatsappNote && (
                    <span className="text-[11px] text-slate-400 dark:text-slate-500 block mt-0.5" data-testid="footer-whatsapp-note">
                      {footerWhatsappNote}
                    </span>
                  )}
                </div>
              </li>
            )}
            {(footerAddr1 || footerAddr2) && (
              <li className="flex items-start gap-2.5">
                <MapPin size={13} className="text-[#1698AC] dark:text-[#4FC6D8] mt-0.5" />
                <div className="min-w-0">
                  <span data-testid="footer-address" className="block leading-tight">
                    {footerAddr1}
                    {footerAddr1 && footerAddr2 && <br />}
                    {footerAddr2}
                  </span>
                  {footerAddressNote && (
                    <span className="text-[11px] text-slate-400 dark:text-slate-500 block mt-0.5" data-testid="footer-address-note">
                      {footerAddressNote}
                    </span>
                  )}
                </div>
              </li>
            )}
          </ul>
        </div>

        {/* Colunas de navegação */}
        {COLS.map((c) => (
          <div key={c.title} data-testid={`footer-col-${c.title.toLowerCase()}`}>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-400 mb-4">{c.title}</p>
            <ul className="space-y-2.5 text-sm text-slate-600 dark:text-slate-300">
              {c.items.map((it) => (
                <li key={it.label + it.to}>
                  <Link to={it.to} className="hover:text-[#1698AC] dark:hover:text-[#4FC6D8] transition-colors">
                    {it.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Linha inferior */}
      <div className="border-t border-slate-200 dark:border-white/10 py-5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <p className="text-xs text-slate-400" data-testid="footer-copyright">
            © {new Date().getFullYear()} INXPEK marca registada. Todos os direitos reservados.
            <span className="mx-2 hidden sm:inline">·</span>
            <span className="block sm:inline mt-1 sm:mt-0">
              Desenvolvido por{" "}
              <a
                href="https://firstmedia.pt/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-firstmedia"
                className="text-slate-500 dark:text-slate-300 hover:text-[#1698AC] dark:hover:text-[#4FC6D8] transition-colors underline underline-offset-2"
              >
                First Media
              </a>
            </span>
          </p>
          <div className="flex items-center gap-5 text-xs text-slate-400">
            <Link to="/politica-privacidade" data-testid="footer-politica" className="hover:text-[#1698AC] dark:hover:text-[#4FC6D8] transition-colors underline underline-offset-2">
              Política de Privacidade · RGPD
            </Link>
            <Link to="/termos-condicoes" data-testid="footer-termos" className="hover:text-[#1698AC] dark:hover:text-[#4FC6D8] transition-colors underline underline-offset-2">
              Termos e Condições
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
